import React, { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import {
  Loader2, Users, DollarSign, TrendingUp, Zap, Play, BarChart3,
  CheckCircle, Clock, Target, Percent, ArrowUpRight, Settings, Power, Info
} from "lucide-react";
import { Card, GoldButton } from "../shared/UIComponents";
import { fmtUSD } from "../../utils/currency";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LOAN_TYPE_LABELS = {
  fix_flip: "Fix & Flip", bridge: "Bridge", dscr: "DSCR", cash_out: "Cash-Out Refi",
  construction: "Construction", ground_up: "Ground Up", multifamily: "Multifamily", commercial: "Commercial",
};

export default function SyndicationDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syndicating, setSyndicating] = useState(null);
  const [triggers, setTriggers] = useState(null);
  const [triggerLoading, setTriggerLoading] = useState(true);
  const [toggling, setToggling] = useState(null);

  const token = localStorage.getItem("ic_token");
  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/capital/syndication/dashboard`, { headers });
      setData(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  const fetchTriggers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/capital/syndication/triggers`, { headers });
      setTriggers(res.data);
    } catch (err) { console.error(err); }
    finally { setTriggerLoading(false); }
  }, []);

  useEffect(() => { fetchData(); fetchTriggers(); }, [fetchData, fetchTriggers]);

  const runSyndication = async (oppId) => {
    setSyndicating(oppId);
    try {
      const res = await axios.post(`${API}/capital/syndicate/${oppId}`, {}, { headers });
      alert(`Syndication complete: ${res.data.investors_matched} investors matched, ${fmtUSD(res.data.total_allocated || 0)} allocated (${res.data.pct_funded}% funded)`);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || "Syndication failed");
    }
    finally { setSyndicating(null); }
  };

  const toggleTrigger = async (stage, currentEnabled) => {
    setToggling(stage);
    try {
      await axios.put(`${API}/capital/syndication/triggers`, { stage, enabled: !currentEnabled }, { headers });
      fetchTriggers();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to update trigger");
    }
    finally { setToggling(null); }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-emerald-700" /></div>;
  if (!data) return null;

  const s = data.summary || {};
  const rounds = data.rounds || [];

  return (
    <div className="space-y-6" data-testid="syndication-dashboard">
      {/* Stage-Based Syndication Triggers */}
      <Card hover={false}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Settings className="h-4 w-4 text-gold-dark" />
            <h4 className="text-sm font-bold text-[#0A0A0A] uppercase tracking-wider">Automated Syndication Triggers</h4>
          </div>
          <span className="text-[10px] text-cream-dark/60 bg-cream border border-cream-warm px-2 py-1 rounded-full">Phase 10A</span>
        </div>
        <p className="text-xs text-[#666] mb-4">
          When enabled, syndication actions fire automatically as deals progress through stages. No manual intervention required.
        </p>
        {triggerLoading ? (
          <div className="flex justify-center py-6"><Loader2 className="h-6 w-6 animate-spin text-emerald-700" /></div>
        ) : triggers?.triggers ? (
          <div className="space-y-3">
            {[
              { stage: "matched", icon: Target, flowLabel: "Create Opportunity & Preview Allocations", color: "bg-blue-50 border-blue-200" },
              { stage: "conditional_approval", icon: CheckCircle, flowLabel: "Lock Investor Allocations", color: "bg-amber-50 border-amber-200" },
              { stage: "clear_to_close", icon: DollarSign, flowLabel: "Execute Capital Deployment", color: "bg-emerald-50 border-emerald-200" },
            ].map((item) => {
              const t = triggers.triggers[item.stage];
              if (!t) return null;
              const Icon = item.icon;
              return (
                <motion.div key={item.stage}
                  className={`rounded-xl border p-4 ${t.enabled ? item.color : "bg-gray-50 border-gray-200"} transition-all`}
                  data-testid={`trigger-${item.stage}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${t.enabled ? "bg-white/80" : "bg-gray-100"}`}>
                        <Icon className={`h-4 w-4 ${t.enabled ? "text-emerald-700" : "text-gray-400"}`} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-bold text-[#333]">{t.label}</span>
                          <span className={`text-[9px] px-1.5 py-0.5 rounded font-bold ${t.enabled ? "bg-emerald-100 text-emerald-700" : "bg-gray-200 text-gray-500"}`}>
                            {t.enabled ? "ACTIVE" : "OFF"}
                          </span>
                        </div>
                        <p className="text-[11px] text-[#777] mt-0.5">{t.description}</p>
                        <div className="flex items-center gap-1 mt-1">
                          <Zap className="h-3 w-3 text-gold-dark" />
                          <span className="text-[10px] font-semibold text-gold-dark">{item.flowLabel}</span>
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => toggleTrigger(item.stage, t.enabled)}
                      disabled={toggling === item.stage}
                      data-testid={`trigger-toggle-${item.stage}`}
                      className={`relative w-12 h-6 rounded-full transition-all duration-300 ${t.enabled ? "bg-emerald-500" : "bg-gray-300"}`}
                    >
                      <motion.div
                        className="absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-md"
                        animate={{ left: t.enabled ? "26px" : "2px" }}
                        transition={{ type: "spring", stiffness: 500, damping: 30 }}
                      />
                      {toggling === item.stage && <Loader2 className="h-3 w-3 animate-spin text-white absolute top-1.5 left-1/2 -translate-x-1/2" />}
                    </button>
                  </div>
                </motion.div>
              );
            })}

            {/* Flow visualization */}
            <div className="mt-4 p-3 rounded-lg bg-cream border border-cream-warm">
              <div className="flex items-center gap-2 mb-2">
                <Info className="h-3 w-3 text-emerald-600" />
                <span className="text-[10px] font-bold text-[#444] uppercase tracking-wider">Automated Flow</span>
              </div>
              <div className="flex items-center gap-1 text-[10px] text-[#666] flex-wrap">
                <span className={`px-2 py-1 rounded ${triggers.triggers.matched?.enabled ? "bg-blue-100 text-blue-700 font-bold" : "bg-gray-100 text-gray-400"}`}>Matched</span>
                <span className="text-gray-300">&#8594;</span>
                <span className="text-[#999]">Create Opportunity</span>
                <span className="text-gray-300">&#8594;</span>
                <span className={`px-2 py-1 rounded ${triggers.triggers.conditional_approval?.enabled ? "bg-amber-100 text-amber-700 font-bold" : "bg-gray-100 text-gray-400"}`}>Conditional Approval</span>
                <span className="text-gray-300">&#8594;</span>
                <span className="text-[#999]">Lock Allocations</span>
                <span className="text-gray-300">&#8594;</span>
                <span className={`px-2 py-1 rounded ${triggers.triggers.clear_to_close?.enabled ? "bg-emerald-100 text-emerald-700 font-bold" : "bg-gray-100 text-gray-400"}`}>Clear to Close</span>
                <span className="text-gray-300">&#8594;</span>
                <span className="text-[#999]">Fund Deal</span>
              </div>
            </div>
          </div>
        ) : (
          <p className="text-sm text-[#666]">Unable to load triggers.</p>
        )}
      </Card>

      {/* KPI Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: "Open Rounds", value: s.open_rounds, icon: Target, color: "text-blue-600", bg: "bg-blue-50" },
          { label: "Funded Rounds", value: s.funded_rounds, icon: CheckCircle, color: "text-emerald-600", bg: "bg-emerald-50" },
          { label: "Auto-Invest Rate", value: `${s.auto_invest_rate || 0}%`, icon: Zap, color: "text-gold-dark", bg: "bg-gold/10" },
          { label: "Auto-Raised", value: `$${((s.auto_raised || 0) / 1e3).toFixed(0)}K`, icon: ArrowUpRight, color: "text-purple-600", bg: "bg-purple-50" },
        ].map((kpi, i) => (
          <motion.div key={kpi.label} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
            className={`rounded-xl border border-cream-warm ${kpi.bg} p-4`} data-testid={`synd-kpi-${kpi.label.toLowerCase().replace(/ /g, '-')}`}>
            <div className="flex items-center gap-2 mb-1">
              <kpi.icon className={`h-4 w-4 ${kpi.color}`} />
              <span className="text-[9px] uppercase tracking-wider text-[#999] font-semibold">{kpi.label}</span>
            </div>
            <div className={`text-2xl font-bold ${kpi.color}`}>{kpi.value}</div>
          </motion.div>
        ))}
      </div>

      {/* Capital Raised Summary */}
      <div className="grid md:grid-cols-2 gap-4">
        <Card hover={false}>
          <div className="flex items-center gap-2 mb-3">
            <DollarSign className="h-4 w-4 text-gold-dark" />
            <span className="text-xs font-bold text-[#444] uppercase tracking-wider">Capital Summary</span>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-xl bg-cream border border-cream-warm p-3 text-center">
              <div className="text-xl font-bold text-[#0A0A0A]">${((s.total_target || 0) / 1e6).toFixed(2)}M</div>
              <div className="text-[9px] text-[#999] uppercase">Target Raise</div>
            </div>
            <div className="rounded-xl bg-cream border border-cream-warm p-3 text-center">
              <div className="text-xl font-bold text-emerald-600">${((s.total_raised || 0) / 1e6).toFixed(2)}M</div>
              <div className="text-[9px] text-[#999] uppercase">Total Raised</div>
            </div>
          </div>
        </Card>
        <Card hover={false}>
          <div className="flex items-center gap-2 mb-3">
            <Users className="h-4 w-4 text-gold-dark" />
            <span className="text-xs font-bold text-[#444] uppercase tracking-wider">Investor Pool</span>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-xl bg-cream border border-cream-warm p-3 text-center">
              <div className="text-xl font-bold text-[#0A0A0A]">{s.total_investors}</div>
              <div className="text-[9px] text-[#999] uppercase">Total Investors</div>
            </div>
            <div className="rounded-xl bg-cream border border-cream-warm p-3 text-center">
              <div className="text-xl font-bold text-emerald-600">{s.auto_invest_investors}</div>
              <div className="text-[9px] text-[#999] uppercase">Auto-Invest ON</div>
            </div>
          </div>
        </Card>
      </div>

      {/* Syndication Rounds Table */}
      {rounds.length === 0 ? (
        <Card hover={false}>
          <div className="text-center py-12">
            <BarChart3 className="h-12 w-12 text-emerald-200 mx-auto mb-4" />
            <h3 className="text-lg font-bold text-[#0A0A0A] mb-2">No Syndication Rounds</h3>
            <p className="text-sm text-[#666]">Create investment opportunities from deals to start syndicating capital.</p>
          </div>
        </Card>
      ) : (
        <Card hover={false}>
          <h4 className="text-sm font-bold text-[#0A0A0A] uppercase tracking-wider mb-4 flex items-center gap-2">
            <BarChart3 className="h-4 w-4 text-gold-dark" /> Syndication Rounds
          </h4>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-cream-warm text-[10px] uppercase tracking-wider text-[#999]">
                  <th className="text-left py-2">Deal</th>
                  <th className="text-center py-2">Status</th>
                  <th className="text-right py-2">Target</th>
                  <th className="text-right py-2">Raised</th>
                  <th className="text-center py-2">% Funded</th>
                  <th className="text-center py-2">Investors</th>
                  <th className="text-center py-2">Auto</th>
                  <th className="text-center py-2">Yield</th>
                  <th className="text-center py-2">Action</th>
                </tr>
              </thead>
              <tbody>
                {rounds.map((r) => {
                  const pctColor = r.pct_funded >= 100 ? "text-emerald-600 bg-emerald-50" : r.pct_funded >= 50 ? "text-blue-600 bg-blue-50" : "text-amber-600 bg-amber-50";
                  return (
                    <tr key={r.opportunity_id} className="border-b border-cream-warm/50 hover:bg-cream/50 transition-colors">
                      <td className="py-3">
                        <div className="font-medium text-[#333]">{r.loan_type_label || LOAN_TYPE_LABELS[r.loan_type] || "Deal"}</div>
                        <div className="text-[10px] text-[#999]">{r.state}</div>
                      </td>
                      <td className="py-3 text-center">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${r.status === "funded" ? "bg-emerald-100 text-emerald-700" : r.status === "open" ? "bg-blue-100 text-blue-700" : "bg-gray-100 text-gray-600"}`}>
                          {r.status}
                        </span>
                      </td>
                      <td className="py-3 text-right text-[#666]">${(r.target_raise / 1e3).toFixed(0)}K</td>
                      <td className="py-3 text-right font-medium text-[#333]">${(r.raised / 1e3).toFixed(0)}K</td>
                      <td className="py-3 text-center">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${pctColor}`}>{r.pct_funded}%</span>
                      </td>
                      <td className="py-3 text-center text-[#666]">{r.investor_count}</td>
                      <td className="py-3 text-center text-purple-600 font-medium">{r.auto_syndicated}</td>
                      <td className="py-3 text-center text-gold-dark">{r.projected_yield || "N/A"}%</td>
                      <td className="py-3 text-center">
                        {r.status === "open" && r.pct_funded < 100 && (
                          <button onClick={() => runSyndication(r.opportunity_id)}
                            disabled={syndicating === r.opportunity_id}
                            data-testid={`syndicate-${r.opportunity_id}`}
                            className="px-3 py-1.5 rounded-lg bg-emerald-100 text-emerald-700 text-[10px] font-bold border border-emerald-200 hover:bg-emerald-200 transition-colors disabled:opacity-50">
                            {syndicating === r.opportunity_id ? <Loader2 className="h-3 w-3 animate-spin inline" /> : <Play className="h-3 w-3 inline mr-1" />}
                            Syndicate
                          </button>
                        )}
                        {r.pct_funded >= 100 && <span className="text-[10px] text-emerald-600 font-bold">Fully Funded</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
